import { EnumsValues } from '../../enums/EnumsValues';
import { useHistory } from 'react-router';
import './Summary.less';
import { useContext, useEffect, useState } from 'react';
import GraphqlService from '../../services/graphql/GraphqlService';
import { ContextApp } from '../../contexts/ContextApp';
import { IProcessItem } from '../../interfaces/Workflow';
import { FeatureFlagWrapper } from '../FeatureFlagWrapper/FeatureFlagWrapper';
import { notificationContext } from '../../contexts/NotificationContext';
import { useFlows } from '../../contexts/WorkflowContext';
import useFeatureFlagConfig from '../FeatureFlagWrapper/hooks/useFeatureFlagConfig';

const Summary = () => {
  const { customRequest, Query } = GraphqlService();
  const { selectedTenantId, user, t } = useContext(ContextApp);
  const { state } = useFlows();
  const { activities, loaded } = state;
  const [cantPendientes, setCantPendientes] = useState(0);
  const [cantVencidos, setCantVencidos] = useState(0);
  const [cantFinalizados, setCantFinalizados] = useState(0);
  const history = useHistory();
  const { openNotification } = useContext(notificationContext);
  const currentDay = new Date();
  const lastWeek = new Date();
  lastWeek.setDate(currentDay.getDate() - 6);

  const groupers = [
    {
      id: EnumsValues.WorkflowTabs.Pendientes,
      name: 'Pendientes',
      color: '#FFFBE6',
      icon: { name: 'schedule', color: '#FAAD14' },
    },
    {
      id: EnumsValues.WorkflowTabs.Vencidos,
      name: 'Vencidos',
      color: '#FFF1F0',
      icon: { name: 'warning', color: '#F5222D' },
    },
    {
      id: EnumsValues.WorkflowTabs.Finalizados,
      name: 'Finalizados',
      color: '#F6FFED',
      icon: { name: 'check_circle', color: '#389E0D' },
    },
  ];

  const handleNavigate = (id: string) => {
    history.push(`/app/flowDesk/activities#${id}`);
  };

  const requestActivities = async () => {
    try {
      const activitiesPromise = await customRequest({
        query: Query.getActivitiesHistory,
        variables: {
          input: {
            tenantId: String(selectedTenantId),
            assignee: String(user!.id),
          },
        },
      }).then((response: any) => {
        const data: IProcessItem[] = [];
        response.forEach((item: any) => {
          data.push({
            id: item.id,
            process: item.flow_name,
            status: {
              id: item.status.id,
              name: item.status.name,
              color:
                item.status.id === 1
                  ? 'yellow'
                  : item.status.id === 2
                  ? 'red'
                  : 'grey',
            },
            activity: item.name,
            asignTo: item.assignee,
            its: item.start_date,
            limitDate: item.due_date,
            description: item.description,
            comment: item.comment,
            reason: item.reason,
            previous_task_comment: item.previous_task_comment,
            previous_task: item.previous_task,
            flowId: item.flow_id,
            end_date_instance: item.end_date_instance,
            assigner: item.assigner,
            assigner_name: item.assigner_name,
          });
        });
        return data;
      });

      const [data] = await Promise.all([activitiesPromise]);

      setCantPendientes(
        data.filter((item) => item.status.id === EnumsValues.ConstNumbers.one)
          .length,
      );
      setCantVencidos(
        data.filter((item) => item.status.id === EnumsValues.ConstNumbers.two)
          .length,
      );
      setCantFinalizados(
        data.filter(
          (item) =>
            item.status.id === EnumsValues.ConstNumbers.three &&
            item.its &&
            new Date(item.its) >= lastWeek &&
            new Date(item.its) <= currentDay,
        ).length,
      );
    } catch (error) {
      openNotification({
        type: 'error',
        msj: 'Ocurrió un error al obtener el resumen de actividades.',
        context: 'summary.requestActivities',
      });
    }
  };

  const { isAvailable: featureEnabled } = useFeatureFlagConfig({
    featureFlag: EnumsValues.FeatureFlags.workflowPage,
    publicAccess: false,
  });

  useEffect(() => {
    if (!featureEnabled) return; // no ejecuto nada si la flag no está activa

    if (!loaded.activities) requestActivities();
    else {
      setCantPendientes(
        activities.filter(
          (item) => item.status.id === EnumsValues.ConstNumbers.one,
        ).length,
      );
      setCantVencidos(
        activities.filter(
          (item) => item.status.id === EnumsValues.ConstNumbers.two,
        ).length,
      );
      setCantFinalizados(
        activities.filter(
          (item) =>
            item.status.id === EnumsValues.ConstNumbers.three &&
            item.its &&
            new Date(item.its) >= lastWeek &&
            new Date(item.its) <= currentDay,
        ).length,
      );
    }
  }, [featureEnabled, loaded.activities, activities]);

  return (
    <FeatureFlagWrapper
      featureFlag={EnumsValues.FeatureFlags.workflowPage}
      publicAccess={false}
    >
      <div className="title-container-summary">
        <h2 className="title-h2">
          {t('entity.braulio')} {t('page.workflow')}
        </h2>
        <p>
          Creá flujos de trabajo a medida: definí responsables, etapas, reglas y
          condiciones específicas para cada caso.
        </p>
      </div>
      <div
        className="summary-container"
        style={{
          backgroundColor: 'white',
          padding: '20px 15px',
          borderRadius: 10,
          width: '100%',
          maxHeight: '183px',
        }}
      >
        <h5 className="summary-section-title ">Mis actividades</h5>
        <div className="cards-container">
          {groupers.map((g) => {
            return (
              <div
                key={g.id}
                className="card"
                style={{
                  backgroundColor: `${g.color}`,
                  borderColor: `${g.color}`,
                }}
                onClick={() => handleNavigate(g.id.toString())}
              >
                <div className="state-container">
                  <span
                    className={`material-symbols-outlined ${
                      g.icon.name !== 'schedule' ? 'summary-icons' : null
                    }`}
                    translate="no"
                    style={{
                      color: `${g.icon.color}`,
                      marginRight: 8,
                    }}
                  >
                    {g.icon.name}
                  </span>
                  <span className="state-name">{g.name}</span>
                </div>

                <div className="amount-container">
                  <span className="activitiesAmount">
                    {g.id === EnumsValues.WorkflowTabs.Pendientes
                      ? cantPendientes
                      : g.id === EnumsValues.WorkflowTabs.Vencidos
                      ? cantVencidos
                      : cantFinalizados}
                  </span>

                  {g.id === EnumsValues.WorkflowTabs.Finalizados && (
                    <div className="info-container">
                      <span
                        className="material-symbols-outlined summary-icon-style"
                        translate="no"
                      >
                        info
                      </span>
                      <span>Últimos 7 días</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </FeatureFlagWrapper>
  );
};

export default Summary;
